/**
 * Public API of the SAE Studio engine (brief Section 9, interface sketch 16.4).
 *
 * Every entry point is implemented behind these signatures and validated
 * against the R oracles or brief-faithful reference implementations
 * (brief 14.1; validation/parity). Implementation modules: direct.ts and
 * deff.ts (9.3, 9.4), transform.ts (9.5), fayherriot.ts (9.6-9.9, 9.16),
 * backtransform.ts (9.10), bootstrap.ts (9.12), benchmarking.ts (9.13),
 * selection.ts (9.14), disclosure.ts (Section 10), analysis.ts (16.4).
 */
import { runAnalysisImpl } from './analysis.js';
import { assembleBootstrapEstimatesImpl, backTransformAreasImpl, } from './backtransform.js';
import { applyBenchmarkingImpl } from './benchmarking.js';
import { parametricBootstrapMseImpl } from './bootstrap.js';
import { smoothDesignEffectsImpl } from './deff.js';
import { computeDirectEstimatesImpl, kornGraubardBounds, STATEWIDE_AREA_ID, } from './direct.js';
import { applyComplementarySuppressionImpl, applyDisclosureRulesImpl, } from './disclosure.js';
import { fitFayHerriotImpl } from './fayherriot.js';
import { selectCovariatesImpl } from './selection.js';
import { transformAreaLevelDomainsImpl, transformDomainsImpl, } from './transform.js';
/** areaId of the statewide domain emitted per cell by computeDirectEstimates (brief 9.3, 9.4). */
export { STATEWIDE_AREA_ID };
/**
 * Korn-Graubard interval bounds for a direct estimate (brief 9.3),
 * re-exported for the disclosure pipelines: the proportion
 * presentation standard defines its width tests on this interval
 * (Tier 2 Item 6).
 */
export { kornGraubardBounds };
/**
 * Direct estimates and Taylor-linearization variances for every domain,
 * computed in one pass on the full design (brief 9.3, MET-02).
 */
export function computeDirectEstimates(respondents, options) {
    return computeDirectEstimatesImpl(respondents, options);
}
/**
 * Smoothed design effect deff_c for each cell represented in the estimates
 * (brief 9.4, MET-03).
 */
export function smoothDesignEffects(estimates, options) {
    return smoothDesignEffectsImpl(estimates, options);
}
/**
 * Carry direct estimates onto the transformed scale, computing effective
 * sample sizes and known sampling variances psi_d (brief 9.5, MET-04).
 */
export function transformDomains(estimates, cellDesignEffects, options) {
    return transformDomainsImpl(estimates, cellDesignEffects, options);
}
/**
 * Convert area-level inputs (published direct estimate, sampling variance,
 * sample size, covariates) into Fay-Herriot fit inputs on the transformed
 * scale (brief 9.5).
 */
export function transformAreaLevelDomains(input, options) {
    return transformAreaLevelDomainsImpl(input, options);
}
/**
 * Fit the Fay-Herriot model for one cell: Fisher-scoring REML with the
 * Li-Lahiri adjusted-REML boundary fallback and the Prasad-Rao fallback,
 * returning EBLUPs, synthetic estimates, analytical MSE components, and
 * diagnostics (brief 9.6-9.9, 9.16; MET-05 to MET-08, MET-15).
 */
export function fitFayHerriot(domains, config) {
    return fitFayHerriotImpl(domains, config);
}
/**
 * Back-transform a fit to the proportion scale with bias correction,
 * the interval-based standard error (reported as the RMSE; directive
 * C-2 terminology) and CV (brief 9.10, MET-09). For linear-scale
 * fits the back-transformation is the identity (brief 9.5 with 9.10;
 * OPEN_QUESTIONS Q44) and the standard error is sqrt(mse_theta)
 * (directive C-3).
 */
export function backTransformAreas(fit, options) {
    return backTransformAreasImpl(fit, options);
}
/**
 * Assemble a cell's reported estimates from a parametric bootstrap
 * (brief 9.12): intervals built on the fitted scale around the
 * EBLUP from the bootstrap mse_theta and mapped back, the reported
 * standard error sqrt(mse_p). The single construction both
 * pipelines use (Tier 2 Item 7; see backtransform.ts).
 */
export function assembleBootstrapEstimates(estimates, fit, boot, options) {
    return assembleBootstrapEstimatesImpl(estimates, fit, boot, options);
}
/**
 * Full-mode parametric bootstrap MSE for one cell (brief 9.12, MET-11).
 * Seeded, with independent reproducible substreams per cell and replicate;
 * results are identical for any number of workers.
 */
export function parametricBootstrapMse(domains, options) {
    return parametricBootstrapMseImpl(domains, options);
}
/**
 * External benchmarking: align the population-weighted average of the area
 * estimates with the target (brief 9.13, MET-12). Returned estimates are
 * stamped with the method in benchmarkingMethod, and their intervals are
 * recomputed around the benchmarked estimates with each estimate's
 * carried mse_theta.
 */
export function applyBenchmarking(estimates, options) {
    return applyBenchmarkingImpl(estimates, options);
}
/**
 * Automatic covariate selection: screening (missingness, variance,
 * collinearity), then best-subset or stepwise search by ML information
 * criterion (brief 9.14, MET-13).
 */
export function selectCovariates(input) {
    return selectCovariatesImpl(input);
}
/**
 * Evaluate the disclosure rules for every domain in fixed order; each rule
 * returns publish, publish with flag, or suppress, and the first rule that
 * fires is stored as the reason (brief 10.1, DIS-01).
 */
export function applyDisclosureRules(inputs, config) {
    return applyDisclosureRulesImpl(inputs, config);
}
/**
 * Complementary suppression: when exactly one level of a subgroup has a
 * suppressed count, also suppress the next-smallest level's count so the
 * value cannot be recovered by subtraction (brief 10.3, DIS-02).
 */
export function applyComplementarySuppression(outcomes, inputs) {
    return applyComplementarySuppressionImpl(outcomes, inputs);
}
/**
 * Run a complete analysis: direct estimation, design effects, transformation,
 * one Fay-Herriot fit per model cell, uncertainty, benchmarking
 * (on by default, decision D-4), estimate policy, and disclosure rules
 * (brief 16.4).
 */
export function runAnalysis(input, config, onProgress) {
    return runAnalysisImpl(input, config, onProgress);
}
//# sourceMappingURL=engine.js.map